import { Box, Button, Paper, Typography } from "@mui/material";

export interface FlagCardAction {
  label: string;
  onClick: () => void;
}

export interface FlagCardProps {
  title: string;
  body: string;
  primary: FlagCardAction;
  secondary?: FlagCardAction;
  tone?: "warn" | "neutral";
}

/**
 * Review flag (design `flagsView`): warn tone on warningBg with a warningLine border, neutral
 * on surfaceAlt; a strong pill and a strong-outline pill for the two actions.
 */
export const FlagCard = ({
  title,
  body,
  primary,
  secondary,
  tone = "neutral",
}: FlagCardProps) => (
  <Paper
    elevation={0}
    data-tone={tone}
    sx={(theme) => ({
      p: "16px 18px",
      borderRadius: "16px",
      display: "flex",
      flexDirection: "column",
      gap: 1,
      bgcolor:
        tone === "warn"
          ? theme.palette.tokens.warningBg
          : theme.palette.surfaceAlt,
      border: `1.5px solid ${
        tone === "warn" ? theme.palette.warningLine : theme.palette.divider
      }`,
    })}
  >
    <Typography
      component="h3"
      sx={(theme) => ({
        m: 0,
        fontSize: 14,
        fontWeight: 800,
        color:
          tone === "warn"
            ? theme.palette.tokens.warningFg
            : theme.palette.text.primary,
      })}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: 13,
        lineHeight: 1.4,
        color: (th) => th.palette.tokens.textMuted,
      }}
    >
      {body}
    </Typography>
    <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        onClick={primary.onClick}
      >
        {primary.label}
      </Button>
      {secondary && (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={secondary.onClick}
          sx={{ borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } }}
        >
          {secondary.label}
        </Button>
      )}
    </Box>
  </Paper>
);

export default FlagCard;
